import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "wouter";
import { Page } from "@/components/Page";
import { TrendingUp, DollarSign, FileText, Briefcase, Calendar, Users, ChevronLeft, ChevronRight, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { MonthView } from "@/features/calendar/components/MonthView";
import { WeekView } from "@/features/calendar/components/WeekView";
import { DayView } from "@/features/calendar/components/DayView";
import { JobCard } from "@/components/JobCard";
import { StatsCard } from "@/components/StatsCard";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, format } from "date-fns";
import { normalizeLegacyStatus, getJobStatusColor } from "@/shared/types";
import type { Job, User } from "@/shared/types";
import { getDashboardSummary } from "../api/dashboardApi";
import type { DashboardData, CalendarView, EventMoveUpdate } from "../types";
import type { PaginatedCustomersResponse } from "@/features/customers/types";
import { ClickableStatCard } from "@/components/ClickableStatCard";
import { SectionHeader } from "@/components/SectionHeader";
import { EmptyState } from "@/components/EmptyState";
import { RecentActivityCard } from "@/components/RecentActivityCard";
import { MetricCard } from "@/components/MetricCard";
import { DashboardSkeleton } from "../components/DashboardSkeleton";
import { usePublicHolidaysForYears } from "@/hooks/usePublicHolidays";

export default function Dashboard() {
  const [, navigate] = useLocation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>("month");
  const { toast } = useToast();
  const { data: publicHolidays = [] } = usePublicHolidaysForYears([date.getFullYear(), date.getFullYear() + 1]);
  
  // Detect mobile screen size
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768; // md breakpoint
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile, default to day view instead of month view
  useEffect(() => {
    if (isMobile && view === "month") {
      setView("day");
    }
  }, [isMobile, view]);

  const loadDashboard = useCallback(async () => {
    try {
      setError("");
      const dashboardData = await getDashboardSummary();
      setData(dashboardData);
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : "Failed to load dashboard";
      console.error("[Dashboard] Load error:", msg);
      setError(msg);
      // Set default empty data to prevent crashes
      setData({
        recentQuotes: [],
        recentJobs: [],
        recentInvoices: [],
        unpaid: { count: 0, total: 0 },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Refetch dashboard data when page becomes visible (e.g., user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadDashboard();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadDashboard]);

  // Fetch team members for staff snapshot
  const { data: staffData = [] } = useQuery<User[]>({
    queryKey: ["/users"],
    retry: false,
  });

  // Fetch jobs for mini calendar
  const { data: jobs = [] } = useQuery<Job[]>({
    queryKey: ["/jobs"],
    retry: false,
  });

  // Fetch customers for job cards
  const { data: customersResponse } = useQuery<PaginatedCustomersResponse>({
    queryKey: ["/customers"],
    retry: false,
  });
  const customers = Array.isArray(customersResponse?.data) ? customersResponse.data : [];
  const totalCustomers = customersResponse?.pagination?.total ?? customers.length;

  if (loading) {
    return (
      <Page title="Dashboard">
        <DashboardSkeleton />
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page title="Dashboard">
        <div className="card bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 py-4 px-6" data-testid="error-state">
          <p className="font-semibold">Error Loading Dashboard</p>
          <p className="text-sm mt-1">{error || "Failed to load dashboard"}</p>
        </div>
      </Page>
    );
  }

  const { recentQuotes, recentJobs, recentInvoices, unpaid, revenue } = data;
  const activeJobs = recentJobs.filter((j) => j.status !== "completed").length;
  
  // Use revenue data from API (based on actual payments) if available, otherwise calculate from invoices
  const totalRevenue = revenue?.total ?? 0;
  const paidAmount = revenue?.paid ?? 0;

  // Transform jobs for calendar
  // Show all jobs that have been scheduled (have a scheduledStartDate)
  // Exclude archived jobs from calendar view
  const events = jobs
    .filter((job) => {
      // Only show jobs that have been scheduled (have a scheduledStartDate)
      // Exclude archived jobs
      return job.scheduledStartDate != null && job.status !== "archived";
    })
    .map(job => ({
      id: job.id,
      jobNumber: job.jobNumber ?? undefined,
      title: job.title,
      date: job.scheduledStartDate ? new Date(job.scheduledStartDate) : new Date(),
      scheduledStartDate: job.scheduledStartDate ? new Date(job.scheduledStartDate) : undefined,
      scheduledEndDate: job.scheduledEndDate ? new Date(job.scheduledEndDate) : undefined,
      status: job.status as any,
      customer: "",
      address: job.address ?? undefined,
      assignedStaff: job.assignedStaffIds?.map((id: string) => staffData.find((u: User) => u.id === id)).filter((u): u is any => Boolean(u)) || [],
      backgroundColor: getJobStatusColor(job.status),
      borderColor: getJobStatusColor(job.status),
    }));

  const staff = (staffData || []).map(u => ({
    ...u,
    firstName: u.firstName ?? "",
    lastName: u.lastName ?? "",
    primaryColor: u.primaryColor ?? undefined,
    workdayStart: u.workdayStart ?? "09:00",
    workdayEnd: u.workdayEnd ?? "17:00",
  })) as any;

  function handleOpenJob(jobId: string) {
    navigate(`/job/${jobId}`);
  }

  function handleDayClick(clickedDate: Date) {
    setDate(clickedDate);
    setView("day");
  }

  function handlePrevious() {
    if (view === "month") {
      setDate(subMonths(date, 1));
    } else if (view === "week") {
      setDate(subWeeks(date, 1));
    } else {
      setDate(subDays(date, 1));
    }
  }

  function handleNext() {
    if (view === "month") {
      setDate(addMonths(date, 1));
    } else if (view === "week") {
      setDate(addWeeks(date, 1));
    } else {
      setDate(addDays(date, 1));
    }
  }

  function handleToday() {
    setDate(new Date());
  }

  async function handleEventMove(update: EventMoveUpdate) {
    try {
      await apiRequest("POST", `/jobs/${update.eventId}/update`, {
        scheduledStartDate: update.newStart.toISOString(),
        scheduledEndDate: update.newEnd.toISOString(),
        overrideConflict: false,
      });

      await queryClient.invalidateQueries({ queryKey: ["/jobs"] });

      toast({
        title: "Job rescheduled",
        description: "The job has been moved successfully.",
      });
    } catch (error: any) {
      console.error("Error moving event:", error);
      
      toast({
        title: "Failed to reschedule",
        description: error?.message || "Failed to reschedule job",
        variant: "destructive",
      });
    }
  }

  function handleCreateJob(startTime: Date, staffId: string) {
    const params = new URLSearchParams({
      startTime: startTime.toISOString(),
      staffId: staffId,
    });
    navigate(`/jobs?action=create&${params.toString()}`);
  }

  function getDateRangeText() {
    if (view === "month") {
      return format(date, "MMMM yyyy");
    } else if (view === "week") {
      return format(date, "MMM d, yyyy");
    } else {
      return format(date, "EEEE, MMMM d, yyyy");
    }
  }

  return (
    <Page title="Dashboard">
      {/* TOP ROW – CLICKABLE SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="dashboard-stats">
        <ClickableStatCard
          title="Active Jobs"
          value={activeJobs}
          icon={Briefcase}
          description="In progress"
          iconColor="text-green-500"
          onClick={() => navigate("/jobs")}
          testId="stat-active-jobs"
        />
        <ClickableStatCard
          title="Quotes"
          value={recentQuotes.length}
          icon={FileText}
          description="Recent"
          iconColor="text-blue-500"
          onClick={() => navigate("/quotes")}
          testId="stat-quotes"
        />
        <ClickableStatCard
          title="Invoices"
          value={recentInvoices.length}
          icon={TrendingUp}
          description="Recent"
          iconColor="text-purple-500"
          onClick={() => navigate("/invoices")}
          testId="stat-invoices"
        />
        <ClickableStatCard
          title="Customers"
          value={totalCustomers}
          icon={Users}
          description="Total"
          iconColor="text-orange-500"
          onClick={() => navigate("/customers")}
          testId="stat-customers"
        />
      </div>

      {/* BOTTOM ROW – CASH FLOW SNAPSHOT */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="cashflow-stats">
        <StatsCard
          title="Total Revenue"
          value={`$${(Number(totalRevenue) || 0).toFixed(2)}`}
          icon={DollarSign}
          description="All payments received"
        />
        <StatsCard
          title="Paid"
          value={`$${(Number(paidAmount) || 0).toFixed(2)}`}
          icon={TrendingUp}
          description="Received"
        />
        <StatsCard
          title="Unpaid"
          value={`$${(Number(unpaid.total) || 0).toFixed(2)}`}
          icon={TrendingDown}
          description={`${unpaid.count} invoices`}
        />
        <StatsCard
          title="Pending Quotes"
          value={recentQuotes.filter(q => q.status === 'sent').length}
          icon={FileText}
          description="Awaiting response"
        />
      </div>

      {/* QUICK ACTIONS ROW */}
      <div className="mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Button 
            onClick={() => navigate("/jobs")} 
            className="gap-2"
            data-testid="button-new-job"
          >
            <Briefcase className="h-4 w-4" />
            New Job
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate("/quotes")}
            className="gap-2"
            data-testid="button-new-quote"
          >
            <FileText className="h-4 w-4" />
            New Quote
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate("/customers")}
            className="gap-2"
            data-testid="button-new-customer"
          >
            <Users className="h-4 w-4" />
            New Customer
          </Button>
        </div>
      </div>

      {/* FULL-WIDTH CALENDAR */}
      <div className="mb-6">
        <div className="bg-white dark:bg-gray-900 rounded-lg border shadow-sm overflow-hidden">
          {/* Calendar Header with Controls */}
          <div className="p-4 border-b bg-gray-50 dark:bg-gray-800">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Calendar className="h-6 w-6" />
                  {getDateRangeText()}
                </h2>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {/* Navigation */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevious}
                  data-testid="button-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToday}
                  data-testid="button-today"
                >
                  Today
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNext}
                  data-testid="button-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>

                {/* View Selector */}
                <div className="flex gap-1 ml-2">
                  {!isMobile && (
                    <button
                      className={`px-3 py-1 rounded border text-sm ${
                        view === "month" ? "bg-blue-600 text-white" : ""
                      }`}
                      onClick={() => setView("month")}
                      data-testid="button-view-month"
                    >
                      Month
                    </button>
                  )}

                  {!isMobile && (
                    <button
                      className={`px-3 py-1 rounded border text-sm ${
                        view === "week" ? "bg-blue-600 text-white" : ""
                      }`}
                      onClick={() => setView("week")}
                      data-testid="button-view-week"
                    >
                      Week
                    </button>
                  )}

                  <button
                    className={`px-3 py-1 rounded border text-sm ${
                      view === "day" || (isMobile && view === "month") ? "bg-blue-600 text-white" : ""
                    }`}
                    onClick={() => setView("day")}
                    data-testid="button-view-day"
                  >
                    Day
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Content */}
          <div className="p-4">
            {events.length === 0 && !isMobile && view === "month" ? (
              <EmptyState
                icon={Calendar}
                title="No jobs scheduled"
                action={{
                  label: "Schedule a job",
                  onClick: () => navigate("/jobs"),
                }}
                className="py-12"
                testId="empty-calendar"
              />
            ) : (
              <div className={`rounded border bg-white dark:bg-gray-950 shadow-sm overflow-hidden`} data-testid="calendar-view">
                {view === "month" && !isMobile && (
                  <MonthView
                    date={date}
                    events={events}
                    staff={staff}
                    holidays={publicHolidays}
                    onChangeDate={setDate}
                    onOpenJob={handleOpenJob}
                    onDayClick={handleDayClick}
                  />
                )}

                {view === "week" && !isMobile && (
                  <WeekView
                    currentDate={date}
                    staff={staff}
                    events={events}
                    holidays={publicHolidays}
                    onEventMove={handleEventMove}
                    onOpenJob={handleOpenJob}
                    onCreateJob={handleCreateJob}
                  />
                )}

                {(view === "day" || isMobile || (view === "week" && isMobile)) && (
                  <DayView
                    date={date}
                    events={events}
                    staff={staff}
                    holidays={publicHolidays}
                    onChangeDate={setDate}
                    onOpenJob={handleOpenJob}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* STAFF SNAPSHOT & QUICK STATS */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="p-4 border-b">
            <h2 className="font-semibold flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team
            </h2>
          </div>
          <div className="p-4">
            {staffData.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No team members"
                action={{
                  label: "Add team members",
                  onClick: () => navigate("/settings"),
                }}
                className="py-4 text-sm"
                testId="empty-staff"
              />
            ) : (
              <div className="space-y-2">
                {staffData.slice(0, 5).map((staff: any) => (
                  <div
                    key={staff.id}
                    className="flex items-center justify-between p-2 rounded hover:bg-muted text-sm"
                    data-testid={`staff-item-${staff.id}`}
                  >
                    <div>
                      <div className="font-medium">
                        {staff.firstName} {staff.lastName}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {staff.role || "staff"}
                      </div>
                    </div>
                    {staff.isActive && (
                      <div className="h-2 w-2 rounded-full bg-green-500" data-testid={`staff-active-${staff.id}`} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        <MetricCard
          label="This Week"
          value={jobs.filter(j => {
            const start = j.scheduledStartDate ? new Date(j.scheduledStartDate) : null;
            if (!start) return false;
            const now = new Date();
            const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 7);
            return start >= weekStart && start < weekEnd;
          }).length}
          description="Jobs scheduled"
          testId="metric-this-week"
        />
        <MetricCard
          label="Today"
          value={jobs.filter(j => {
            const start = j.scheduledStartDate ? new Date(j.scheduledStartDate) : null;
            if (!start) return false;
            const today = new Date();
            return start.toDateString() === today.toDateString();
          }).length}
          description="Jobs today"
          testId="metric-today"
        />
        <MetricCard
          label="Upcoming"
          value={jobs.filter(j => {
            const start = j.scheduledStartDate ? new Date(j.scheduledStartDate) : null;
            if (!start) return false;
            return start > new Date();
          }).length}
          description="Future jobs"
          testId="metric-upcoming"
        />
      </div>

      {/* RECENT ACTIVITY SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Quotes */}
        <div className="card">
          <SectionHeader
            title="Recent Quotes"
            onViewAll={() => navigate("/quotes")}
            testId="section-recent-quotes"
          />

          {recentQuotes.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No quotes yet"
              className="text-sm py-4"
              testId="empty-quotes"
            />
          ) : (
            <div className="space-y-2">
              {recentQuotes.map((q) => (
                <RecentActivityCard
                  key={q.id}
                  title={q.quoteNumber || "Quote"}
                  subtitle={(q as any).customerName || "Client"}
                  badge={
                    (q as any).xeroQuoteId
                      ? {
                          label: "From Xero",
                          variant: "outline",
                          className: "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700",
                        }
                      : undefined
                  }
                  metadata={[
                    {
                      label: "Date",
                      value: q.createdAt ? new Date(q.createdAt).toLocaleDateString() : "",
                    },
                    {
                      label: "Total",
                      value: `$${((Number(q.total) || 0) / 100).toFixed(2)}`,
                    },
                  ]}
                  footer={
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary" className="text-xs">
                        {q.status || "draft"}
                      </Badge>
                      {(q as any).address && (
                        <div className="text-xs text-muted-foreground truncate">
                          {(q as any).address}
                        </div>
                      )}
                    </div>
                  }
                  onClick={() => navigate(`/quotes/${q.id}`)}
                  testId={`quote-item-${q.id}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Recent Jobs */}
        <div className="card">
          <SectionHeader
            title="Recent Jobs"
            onViewAll={() => navigate("/jobs")}
            testId="section-recent-jobs"
          />

          {recentJobs.length === 0 ? (
            <EmptyState
              icon={Briefcase}
              title="No jobs yet"
              className="text-sm py-4"
              testId="empty-jobs"
            />
          ) : (
            <div className="space-y-3">
              {recentJobs.map((j) => {
                const customer = Array.isArray(customers) ? customers.find((c) => c.id === j.customerId) : null;
                
                // Get assigned staff info
                const assignedStaff = j.assignedStaffIds?.map((id: string) => 
                  staffData.find((u: any) => u.id === id)
                ).filter((u: any): u is User => Boolean(u)) || [];

                const assignedStaffNames = assignedStaff.length > 0
                  ? assignedStaff.map((s: User) => `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.username)
                  : (j.assignedTo ? [staffData.find((u: any) => u.id === j.assignedTo)].map((u: User | undefined) => 
                      u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username : 'Unknown'
                    ) : []);

                // Format date
                const formatJobDate = () => {
                  if (!j.scheduledStartDate) return "Not scheduled";
                  
                  const startDate = typeof j.scheduledStartDate === 'string' 
                    ? new Date(j.scheduledStartDate) 
                    : j.scheduledStartDate;
                  
                  if (j.scheduledEndDate) {
                    const endDate = typeof j.scheduledEndDate === 'string' 
                      ? new Date(j.scheduledEndDate) 
                      : j.scheduledEndDate;
                    
                    if (startDate.toDateString() !== endDate.toDateString()) {
                      return `${format(startDate, "MMM d")} - ${format(endDate, "MMM d, yyyy")}`;
                    }
                  }
                  
                  return format(startDate, "EEE, MMM d, yyyy");
                };

                return (
                  <JobCard
                    key={j.id}
                    id={j.id!}
                    jobNumber={j.jobNumber ?? undefined}
                    title={j.title}
                    description={j.description || undefined}
                    customerName={customer?.name || (j as any).customerName || "Client"}
                    customerPhone={customer?.phone || undefined}
                    customerEmail={customer?.email || undefined}
                    status={normalizeLegacyStatus(j.status) as any}
                    date={formatJobDate()}
                    assignedStaff={assignedStaff as any}
                    assignedStaffNames={assignedStaffNames.filter((n: any): n is string => typeof n === 'string')}
                    labels={j.tags?.filter((t: any): t is string => t !== null) || []}
                    isMultiDay={j.scheduledStartDate !== j.scheduledEndDate}
                    isRecurring={j.isRepeating || false}
                    address={j.address || undefined}
                    invoiceStatus={j.invoiceStatus ? j.invoiceStatus : undefined}
                    onSelect={() => navigate(`/job/${j.id}`)}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Invoices */}
        <div className="card">
          <SectionHeader
            title="Recent Invoices"
            onViewAll={() => navigate("/invoices")}
            testId="section-recent-invoices"
          />

          {recentInvoices.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No invoices yet"
              className="text-sm py-4"
              testId="empty-invoices"
            />
          ) : (
            <div className="space-y-2">
              {recentInvoices.map((inv) => (
                <RecentActivityCard
                  key={inv.id}
                  title={inv.invoiceNumber || "Invoice"}
                  subtitle={(inv as any).customerName || "Client"}
                  badge={{
                    label: inv.status || "draft",
                    variant: "secondary",
                  }}
                  metadata={[
                    {
                      label: "Due",
                      value: inv.dueDate
                        ? new Date(inv.dueDate).toLocaleDateString()
                        : inv.invoiceDate
                        ? new Date(inv.invoiceDate).toLocaleDateString()
                        : "No date",
                    },
                    {
                      label: "Outstanding",
                      value: `$${((Math.max(0, (Number(inv.total) || 0) - (Number(inv.paidAmount) || 0)) / 100)).toFixed(2)}`,
                    },
                  ]}
                  footer={
                    (inv as any).address && (
                      <div className="text-xs text-muted-foreground truncate">
                        {(inv as any).address}
                      </div>
                    )
                  }
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  testId={`invoice-item-${inv.id}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

